'use client';

import type React from 'react';
import { Calendar, Mail } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useContactFormAction } from '@/src/components/features/property/ContactForm/useContactFormAction';
import { useTourAction } from '@/src/components/features/property/TourForm';
import { useApplyAction } from '@/src/components/features/property/ApplyForm';
import { useCurrentUser } from '@/src/lib/hooks/query';
import { useScheduledToursForUser } from '@/src/lib/hooks/query/useHpToursQuery';
import { isUserOnWaitlist, isWaitlistedListing } from '@/src/components/features/property/lib/waitlist';
import { getScheduledTour } from '@/src/components/features/property/TourForm/tourUtils';
import { hasBookableTourProvider, type InstantTourProvidersLike } from '@/src/lib/utils/instantTourProviders';

export type ListingCTAId = 'contact' | 'tour' | 'apply';

export type ListingCTA = {
  id: ListingCTAId;
  label: string;
  ariaLabel: string;
  icon: React.ReactNode;
  onClick: () => void;
  emphasis: 'filled' | 'outlined';
  disabled?: boolean;
  ariaBusy?: boolean;
};

type UseListingCTAsResult = {
  ctas: Array<ListingCTA>;
  overlays: React.ReactNode;
};

const RENTAL_APPLICATION_STATUS_ACCEPTED = 'accepted';

const shouldShowApplyCTA = (rentalApplicationStatus?: string | null): boolean =>
  rentalApplicationStatus?.toLowerCase() === RENTAL_APPLICATION_STATUS_ACCEPTED;

/**
 * useListingCTAs — computes the HDP action-bar CTAs (contact / tour / apply)
 * for a listing and returns the overlay nodes the caller must render.
 *
 * Tour is only included when the listing has instant tour providers. Apply
 * is only included for listings accepting Zillow applications, matching the
 * legacy `shouldShowApplyButton` rentalApplicationStatus === "accepted" gate.
 */
export const useListingCTAs = (
  listing: PropertyDetailListing | null | undefined,
  triggerLocation = 'home_details_halo',
): UseListingCTAsResult => {
  const t = useTranslations('hdp');
  const { data: currentUser } = useCurrentUser();
  const scheduledToursQuery = useScheduledToursForUser(currentUser?.loggedIn ?? false);
  const { data: scheduledTours } = scheduledToursQuery;
  const { openContactForm, contactFormOverlay, identityResolved } = useContactFormAction(listing, triggerLocation);
  const { openTour, tourOverlay, isTourReady } = useTourAction(listing, triggerLocation);
  const { openApply, applyOverlay } = useApplyAction(listing, triggerLocation);

  const overlays = (
    <>
      {contactFormOverlay}
      {tourOverlay}
      {applyOverlay}
    </>
  );

  if (!listing || listing.active === false) {
    return { ctas: [], overlays };
  }

  const isWaitlisted = isWaitlistedListing(listing);
  const contactCta =
    isWaitlisted && isUserOnWaitlist(listing)
      ? null
      : ({
          id: 'contact',
          label: isWaitlisted ? t('joinWaitlist') : t('message'),
          ariaLabel: isWaitlisted ? t('joinWaitlistAriaLabel') : t('messageProperty'),
          icon: <LucideIcon icon={Mail} size="sm" />,
          onClick: openContactForm,
          emphasis: 'filled',
          disabled: !identityResolved,
          ariaBusy: !identityResolved,
        } satisfies ListingCTA);

  const ctas: Array<ListingCTA> = contactCta ? [contactCta] : [];

  const providers = (listing as { details?: { instantTourProviders?: InstantTourProvidersLike } })?.details
    ?.instantTourProviders;
  const scheduledTour = getScheduledTour(listing.aliasEncoded, scheduledTours);

  if (hasBookableTourProvider(providers) && !scheduledTour) {
    ctas.push({
      id: 'tour',
      label: t('bookTour'),
      ariaLabel: t('bookTourAriaLabel'),
      icon: <LucideIcon icon={Calendar} size="sm" />,
      onClick: openTour,
      emphasis: 'outlined',
      disabled: !isTourReady || (currentUser?.loggedIn === true && !scheduledToursQuery.isSuccess),
      ariaBusy: !isTourReady || (currentUser?.loggedIn === true && !scheduledToursQuery.isSuccess),
    });
  }

  const rentalApplicationStatus = (listing as { rentalApplicationStatus?: string })?.rentalApplicationStatus;
  if (shouldShowApplyCTA(rentalApplicationStatus)) {
    ctas.push({
      id: 'apply',
      label: t('apply'),
      ariaLabel: t('applyNow'),
      icon: null,
      onClick: openApply,
      emphasis: 'outlined',
    });
  }

  return { ctas, overlays };
};
